/**
 * React Query hook for Google Calendar list
 * Fetches the list of available Google calendars when user is connected
 * Provides automatic caching and background sync
 */

import { useQuery } from '@tanstack/react-query';
import { listEvents } from 'services/Calender/calendarServices';
import { queryKeys } from 'hooks/config/queryClient';

/**
 * Fetches Google Calendar list from API
 * @returns {Promise<Array>} Array of calendar options
 */
const fetchGoogleCalendars = async () => {
  try {
    const response = await listEvents({ list_google_calendars: true });
    const calendars = response?.data || [];

    // Transform to dropdown format
    const calendarOptions = calendars.map((cal) => ({
      label: cal?.name,
      value: cal?.id,
      primary: cal?.primary || false,
      ...cal,
    }));

    return calendarOptions;
  } catch (err) {
    console.error('Error fetching Google calendars:', err);
    throw err;
  }
};

/**
 * Hook to fetch Google Calendar list using React Query
 * @param {boolean} isConnected - Whether Google Calendar is connected
 * @returns {Object} - { googleCalendars, isLoading, error, refetch }
 */
export const useGoogleCalendarList = (isConnected = false) => {
  const { data, isLoading, error, refetch } = useQuery({
    queryKey: queryKeys.googleCalendars.list(),
    queryFn: fetchGoogleCalendars,
    enabled: isConnected, // Only fetch when connected
    staleTime: 5 * 60 * 1000, // 5 minutes
    cacheTime: 10 * 60 * 1000, // 10 minutes
    retry: 2,
    refetchOnWindowFocus: false,
  });

  return {
    googleCalendars: data || [],
    isLoading,
    error,
    refetch,
  };
};

export default useGoogleCalendarList;
